import type { Ref, RefObject } from "react"

import { CoviVizChartBody, parseChartSpec } from "@/app-covi"

import type { NotebookExport } from "../utils/notebookExport"

/** Render settled copies from the saved data, independent of collapsed preview cells. */
export function NotebookExportCharts({
  notebook,
  ref,
}: {
  notebook: NotebookExport
  ref: Ref<HTMLDivElement>
}) {
  return (
    <div
      ref={ref}
      aria-hidden
      data-notebook-export-charts
      className="coval-theme-light pointer-events-none fixed -left-[10000px] top-0 w-[900px] bg-surface-default"
    >
      {notebook.sections.flatMap((section, sectionIndex) =>
        section.cells.map(({ id, cell }) => {
          if (cell.kind !== "chart") {
            return null
          }
          const spec = parseChartSpec(cell.spec, { allowReportColors: true })
          return spec && spec.chart !== "stat" ? (
            <div key={`${sectionIndex}:${id}`} data-notebook-chart={id}>
              <CoviVizChartBody
                spec={spec}
                height={Math.max(
                  360,
                  spec.opts?.horizontal ? spec.data.length * 38 + 80 : 360,
                )}
                animate={false}
                lightTheme
                showValueLabels={false}
                paletteOnly={notebook.revision === undefined}
                reportColors={notebook.revision !== undefined}
              />
            </div>
          ) : null
        }),
      )}
    </div>
  )
}

export async function waitForExportCharts(
  ref: RefObject<HTMLDivElement | null>,
  notebook: NotebookExport,
): Promise<HTMLDivElement> {
  await document.fonts.ready
  const expected = notebook.sections.flatMap((section) =>
    section.cells.filter(({ cell }) => {
      if (cell.kind !== "chart") {
        return false
      }
      const spec = parseChartSpec(cell.spec, { allowReportColors: true })
      return spec && spec.chart !== "stat"
    }),
  ).length
  // React commits the export copies, then ResizeObserver gives recharts its width.
  const deadline = Date.now() + 10_000
  while (Date.now() < deadline) {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 50)
    })
    const root = ref.current
    const charts = root?.querySelectorAll("[data-notebook-chart]")
    if (
      root &&
      charts?.length === expected &&
      [...charts].every(
        (chart) =>
          (chart.querySelector("svg.recharts-surface")?.getBoundingClientRect()
            .width ?? 0) > 0,
      )
    ) {
      return root
    }
  }
  throw new Error(
    "Could not prepare the notebook charts. Try downloading again.",
  )
}
